import type { OperationScenarioName } from './userbi.js';

// ---------------------------------------------------------------------------
// Operational (scenario) telemetry registry.
//
// Scenario NAMES are a closed string union so operational telemetry stays
// consistent and greppable. Register a new scenario by adding it to
// SCENARIO_NAMES — the `ScenarioName` union updates automatically.
//
// Each scenario INVOCATION gets a fresh runtime queryId (correlation GUID)
// generated by `scenarioFactory.newScenario`; the name is the stable, typed
// identifier.
// ---------------------------------------------------------------------------

/** Typed union of registered scenario names. */
export enum ScenarioName {
  AppInit = 'app_init',

  AgentEvaluationSubmit = 'agent_evaluation_submit',
  EvaluationTemplateQueue = 'evaluation_template_queue',
  SharedSignalsExport = 'shared_signals_export',

  // ── Datalake: dataset lifecycle (custom-named ops not covered by the
  //    operation_<entity>_<op> convention) ────────────────────────────────
  DatalakeCreateDataset = 'operation_dataset_create',
  DatalakeUpdateDataset = 'operation_dataset_update',
  DatalakePublishDataset = 'operation_dataset_publish',
  DatalakeDemoteDataset = 'operation_dataset_demote',
  DatalakeDeleteDataset = 'operation_dataset_delete',
  DatalakeDeleteParticipantData = 'datalake_delete_participant_data',
  DatalakeSaveAttributes = 'datalake_save_attributes',

  // ── Datalake: data entries & entry files ─────────────────────────────────
  DatalakeCreateDataEntry = 'operation_dataset_entry_create',
  DatalakeUpdateDataEntry = 'operation_dataset_entry_update',
  DatalakeDeleteDataEntry = 'operation_dataset_entry_delete',
  DatalakeHardDeleteDataEntry = 'datalake_hard_delete_data_entry',
  DatalakeDownloadFile = 'datalake_download_file',
  DatalakeDeleteDataEntryFile = 'datalake_delete_data_entry_file',

  // ── Datalake: annotation sets ────────────────────────────────────────────
  DatalakeCreateAnnotationSet = 'datalake_create_annotation_set',
  DatalakeUpdateAnnotationSet = 'datalake_update_annotation_set',
  DatalakePublishAnnotationSet = 'datalake_publish_annotation_set',
  DatalakeDeleteAnnotationSet = 'datalake_delete_annotation_set',
  DatalakeCreateAnnotationSchema = 'datalake_create_annotation_schema',

  // ── Datalake: annotation data & annotation files ─────────────────────────
  DatalakeCreateAnnotationData = 'datalake_create_annotation_data',
  DatalakeUpdateAnnotationData = 'datalake_update_annotation_data',
  DatalakeDeleteAnnotationData = 'datalake_delete_annotation_data',
  DatalakeDeleteAnnotationFile = 'datalake_delete_annotation_file',
  DatalakeUploadAnnotationFile = 'datalake_upload_annotation_file',
  DatalakeDownloadAnnotationFile = 'datalake_download_annotation_file',

  // ── Datalake: multi-call transfer flows (P3, mark()-instrumented) ─────────
  DatalakeUploadFolder = 'datalake_upload_folder',
  DatalakeDownloadDataset = 'datalake_download_dataset',
  DatalakeUploadFile = 'datalake_upload_file',
  DatalakeUploadAnnotations = 'datalake_upload_annotations',
  DatalakeDownloadAnnotations = 'datalake_download_annotations',

  // ── Test sets ────────────────────────────────────────────────────────────
  TestsetCreate = 'operation_test_set_create',
  TestsetPublish = 'operation_test_set_publish',
  TestsetDemote = 'operation_test_set_demote',
  TestsetDelete = 'operation_test_set_delete',
  TestsetAddData = 'testset_add_data',
  TestsetDeleteData = 'operation_test_set_entry_delete',

  // ── Permissions ──────────────────────────────────────────────────────────
  PermissionUpdate = 'operation_permission_update',
}

export type ScenarioIdentifier = ScenarioName | OperationScenarioName;
